import { useCallback, useEffect, useRef, useState } from "react";

import { Application } from "@core/Application";
import { Permissions } from "@models/Permissions";
import { ShiftRecipe } from "@models/QontactPoint";
import { useHasPermission } from "@hooks/useHasPermission";
import useDebouncedCallback from "@hooks/useDebouncedCallback";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import {
    QontactPointSearchResult,
    QontactPointShiftsCounts,
    QontactPointShiftsFilter,
} from "@services/AlgoliaService";

export const QONTACT_POINTS_PAGE_SIZE = 50;

export type QontactPointListFilter = "all" | QontactPointShiftsFilter;

/** Row of the list. Same shape the detail screen receives in the navigation state. */
export interface QontactPointListItem {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    buildingID: string;
    functionalUnitID: string;
    totalShifts: number;
    triggerDistance?: number;
    shifts?: ShiftRecipe[];
}

const toListItem = (hit: QontactPointSearchResult): QontactPointListItem => ({
    id: hit.id,
    name: hit.buildingName,
    latitude: hit.location?.latitude,
    longitude: hit.location?.longitude,
    buildingID: hit.buildingID,
    functionalUnitID: hit.functionalUnitID,
    // shiftTotal can be missing if the index was never synced: show "Sin turnos"
    totalShifts: hit.shiftTotal ?? 0,
    triggerDistance: hit.triggerDistance,
    shifts: [],
});

const emptyCounts: QontactPointShiftsCounts = { all: 0, withShifts: 0, withoutShifts: 0 };

/**
 * Qontact Points list (Algolia search, as today): search by name, filter by
 * with/without shifts, infinite scroll by pages, create and delete.
 */
export const useQontactPointListScreenViewModel = (orgID: string) => {
    const canManage = useHasPermission(Permissions.QontactPoints.MANAGE) !== false;

    const [searchInput, setSearchInput] = useState("");
    const [searchField, setSearchField] = useState("");
    const [filter, setFilter] = useState<QontactPointListFilter>("all");
    const [qontactPoints, setQontactPoints] = useState<QontactPointListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [counts, setCounts] = useState<QontactPointShiftsCounts>(emptyCounts);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const [qontactPointToDelete, setQontactPointToDelete] = useState<QontactPointListItem | null>(null);

    const loadedPagesRef = useRef<Set<number>>(new Set());
    const loadingPagesRef = useRef<Set<number>>(new Set());
    // Each new search/filter/reload bumps this so late answers of the previous one are ignored
    const generationRef = useRef(0);

    const fetchPage = useCallback(async (page: number) => {
        if (loadedPagesRef.current.has(page) || loadingPagesRef.current.has(page)) {
            return;
        }
        const generation = generationRef.current;
        loadingPagesRef.current.add(page);

        try {
            const algoliaService = Application.shared().coreDependencies.algoliaService;
            const result = await algoliaService.searchQontactPointsV2(orgID, searchField, page, QONTACT_POINTS_PAGE_SIZE, {
                shifts: filter === "all" ? undefined : filter,
                includeCounts: page === 0,
            });
            if (generation !== generationRef.current) {
                return;
            }

            const items = (result.hits ?? []).map(toListItem);
            setQontactPoints((previous) => {
                const next = [...previous];
                next.splice(page * QONTACT_POINTS_PAGE_SIZE, items.length, ...items);
                return next;
            });
            setTotal(result.nbHits ?? 0);
            if (result.counts) {
                setCounts(result.counts);
            }
            loadedPagesRef.current.add(page);
        } catch (error) {
            if (generation === generationRef.current && page === 0) {
                ToastManager.shared().showErrorMessage("No se pudieron cargar los Qontact Points".localized());
            }
            console.error(error);
        } finally {
            loadingPagesRef.current.delete(page);
            if (page === 0 && generation === generationRef.current) {
                setLoading(false);
            }
        }
    }, [orgID, searchField, filter]);

    // New search, filter, organization or reload: start again from page 0
    useEffect(() => {
        generationRef.current += 1;
        loadedPagesRef.current.clear();
        loadingPagesRef.current.clear();
        setQontactPoints([]);
        setTotal(0);
        setLoading(true);
        void fetchPage(0);
    }, [fetchPage, reloadKey]);

    const debouncedSetSearchField = useDebouncedCallback((value: string) => setSearchField(value.trim()), 500);

    const onSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSetSearchField(value);
    };

    const loadMoreItems = async (startIndex: number, stopIndex: number) => {
        const startPage = Math.floor(startIndex / QONTACT_POINTS_PAGE_SIZE);
        const endPage = Math.floor(stopIndex / QONTACT_POINTS_PAGE_SIZE);
        const pages: Promise<void>[] = [];
        for (let page = startPage; page <= endPage; page++) {
            pages.push(fetchPage(page));
        }
        await Promise.all(pages);
    };

    const isItemLoaded = (index: number) => qontactPoints[index] !== undefined;

    const refresh = () => setReloadKey((key) => key + 1);

    const [isExporting, setIsExporting] = useState(false);

    /** Excel with every qontact point of the current search / filter (not only the loaded pages) */
    const exportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const algoliaService = Application.shared().coreDependencies.algoliaService;
            const exportPageSize = 1000; // Algolia's max hits per page
            const hits: QontactPointSearchResult[] = [];
            for (let page = 0; ; page++) {
                const result = await algoliaService.searchQontactPointsV2(orgID, searchField, page, exportPageSize, {
                    shifts: filter === "all" ? undefined : filter,
                });
                const pageHits = result.hits ?? [];
                hits.push(...pageHits);
                if (pageHits.length < exportPageSize || hits.length >= (result.nbHits ?? 0)) {
                    break;
                }
            }
            const { downloadQontactPointsExcel } = await import("@components/QontactPoints/QontactPointsExcel");
            downloadQontactPointsExcel(hits);
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudieron exportar los Qontact Points".localized());
        } finally {
            setIsExporting(false);
        }
    };

    const confirmDelete = async () => {
        if (!qontactPointToDelete) {
            return;
        }
        try {
            await Application.shared().coreDependencies.qontactPointsService.deleteQontactPoint(qontactPointToDelete.id);
            ToastManager.shared().showSuccessMessage("Qontact Point eliminado".localized());
            setQontactPointToDelete(null);
            refresh();
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("No se pudo eliminar el Qontact Point".localized());
        }
    };

    return {
        canManage,
        searchInput,
        searchField,
        onSearchChange,
        filter,
        setFilter,
        counts,
        qontactPoints,
        total,
        loading,
        // Remounts the virtualized list (and its infinite loader cache) on every new search
        listKey: `${orgID}|${searchField}|${filter}|${reloadKey}`,
        loadMoreItems,
        isItemLoaded,
        refresh,
        isExporting,
        exportExcel,
        qontactPointToDelete,
        askDelete: setQontactPointToDelete,
        cancelDelete: () => setQontactPointToDelete(null),
        confirmDelete,
    };
};
